import { useState } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { X, Key, Link2, Plus, CheckCircle, ListPlus, Trash2 } from 'lucide-react'
import { brevoApi, BrevoList, SegmentMapping } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'

interface BrevoConfigModalProps {
  isOpen: boolean
  onClose: () => void
  lists: BrevoList[]
  mappings: SegmentMapping[]
}

export default function BrevoConfigModal({
  isOpen,
  onClose,
  lists,
  mappings
}: BrevoConfigModalProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState<'api' | 'mappings'>('api')
  const [apiKey, setApiKey] = useState('')
  const [newListName, setNewListName] = useState('')

  // Get current status
  const statusQuery = useQuery({
    queryKey: ['brevo-status'],
    queryFn: brevoApi.getStatus
  })

  const configQuery = useQuery({
    queryKey: ['brevo-config'],
    queryFn: brevoApi.getConfig
  })

  // Mutations
  const setApiKeyMutation = useMutation({
    mutationFn: (key: string) => brevoApi.setApiKey(key),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brevo-status'] })
      queryClient.invalidateQueries({ queryKey: ['brevo-config'] })
      queryClient.invalidateQueries({ queryKey: ['brevo-lists'] })
      toast.success(`Conectado a Brevo: ${data.account?.company_name || 'OK'}`)
      setApiKey('')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    }
  })

  const createListMutation = useMutation({
    mutationFn: (name: string) => brevoApi.createList(name),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brevo-lists'] })
      toast.success(`Lista "${data.name}" creada correctamente`)
      setNewListName('')
    },
    onError: (error: any) => {
      toast.error('Error creando lista', error.response?.data?.detail || error.message)
    }
  })

  const setMappingMutation = useMutation({
    mutationFn: (data: { segment_name: string; brevo_list_id: number; brevo_list_name: string }) =>
      brevoApi.setMapping(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brevo-config'] })
      toast.success('Mapeo guardado')
    },
    onError: (error: any) => {
      toast.error('Error guardando mapeo', error.response?.data?.detail || error.message)
    }
  })

  const clearMappingMutation = useMutation({
    mutationFn: (segmentName: string) => brevoApi.clearMapping(segmentName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brevo-config'] })
      toast.success('Mapeo eliminado')
    },
    onError: (error: any) => {
      toast.error('Error eliminando mapeo', error.response?.data?.detail || error.message)
    }
  })

  const handleSetApiKey = (e: React.FormEvent) => {
    e.preventDefault()
    if (!apiKey.trim()) {
      toast.error('Error', 'La API key es requerida')
      return
    }
    setApiKeyMutation.mutate(apiKey.trim())
  }

  const handleCreateList = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newListName.trim()) {
      toast.error('Error', 'El nombre de la lista es requerido')
      return
    }
    createListMutation.mutate(newListName.trim())
  }

  const handleMappingChange = (segmentName: string, listId: number | null) => {
    if (listId === null) {
      clearMappingMutation.mutate(segmentName)
    } else {
      const list = lists.find(l => l.id === listId)
      if (list) {
        setMappingMutation.mutate({
          segment_name: segmentName,
          brevo_list_id: listId,
          brevo_list_name: list.name
        })
      }
    }
  }

  const isConnected = statusQuery.data?.connected === true

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-slate-500/75 dark:bg-slate-900/75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-slate-800 rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Configuracion de Brevo
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setActiveTab('api')}
              className={cn(
                "flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === 'api'
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-slate-500 hover:text-gray-700 dark:text-slate-400"
              )}
            >
              <Key className="w-4 h-4 inline-block mr-2" />
              API Key
            </button>
            <button
              onClick={() => setActiveTab('mappings')}
              className={cn(
                "flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeTab === 'mappings'
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-slate-500 hover:text-gray-700 dark:text-slate-400"
              )}
            >
              <Link2 className="w-4 h-4 inline-block mr-2" />
              Mapeo de Segmentos
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {activeTab === 'api' && (
              <div className="space-y-6">
                {/* Current Status */}
                <div className={cn(
                  "p-4 rounded-lg border",
                  isConnected
                    ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800"
                    : "bg-slate-50 border-gray-200 dark:bg-slate-800 dark:border-gray-700"
                )}>
                  <div className="flex items-center gap-3">
                    {isConnected ? (
                      <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <Key className="w-5 h-5 text-slate-400" />
                    )}
                    <div>
                      <p className={cn(
                        "font-medium",
                        isConnected ? "text-green-800 dark:text-green-200" : "text-slate-700 dark:text-slate-300"
                      )}>
                        {isConnected ? 'Conectado' : 'No configurado'}
                      </p>
                      {isConnected && statusQuery.data?.email && (
                        <p className="text-sm text-green-600 dark:text-green-400">
                          {statusQuery.data.email}
                        </p>
                      )}
                      {configQuery.data?.api_key_preview && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 font-mono">
                          {configQuery.data.api_key_preview}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Set API Key */}
                <form onSubmit={handleSetApiKey}>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {isConnected ? 'Cambiar API Key' : 'Configurar API Key'}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="xkeysib-..."
                      className="input flex-1 font-mono text-sm"
                    />
                    <button
                      type="submit"
                      disabled={setApiKeyMutation.isPending || !apiKey.trim()}
                      className="btn btn-primary"
                    >
                      {setApiKeyMutation.isPending ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Obtiene tu API key en{' '}
                    <a
                      href="https://app.brevo.com/settings/keys/api"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      Brevo Dashboard &gt; SMTP & API
                    </a>
                  </p>
                </form>
              </div>
            )}

            {activeTab === 'mappings' && (
              <div className="space-y-6">
                {!isConnected ? (
                  <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                    <Key className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Configura primero la API key para gestionar los mapeos</p>
                  </div>
                ) : (
                  <>
                    {/* Create New List */}
                    <form onSubmit={handleCreateList} className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Crear nueva lista en Brevo
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newListName}
                          onChange={(e) => setNewListName(e.target.value)}
                          placeholder="Nombre de la lista"
                          className="input flex-1"
                        />
                        <button
                          type="submit"
                          disabled={createListMutation.isPending || !newListName.trim()}
                          className="btn btn-secondary"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Crear
                        </button>
                      </div>
                    </form>

                    {/* Segment Mappings */}
                    <div>
                      <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                        Mapear Segmentos RFM a Listas
                      </h3>
                      <div className="space-y-3">
                        {mappings.map((mapping) => (
                          <div
                            key={mapping.segment_name}
                            className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                          >
                            <span className="font-medium text-slate-900 dark:text-white min-w-32">
                              {mapping.segment_name}
                            </span>
                            <select
                              value={mapping.brevo_list_id || ''}
                              onChange={(e) => handleMappingChange(
                                mapping.segment_name,
                                e.target.value ? parseInt(e.target.value) : null
                              )}
                              className="input flex-1"
                              disabled={setMappingMutation.isPending || clearMappingMutation.isPending}
                            >
                              <option value="">-- Sin asignar --</option>
                              {lists.map((list) => (
                                <option key={list.id} value={list.id}>
                                  {list.name} ({list.total_subscribers} contactos)
                                </option>
                              ))}
                            </select>
                            {mapping.brevo_list_id && (
                              <button
                                onClick={() => handleMappingChange(mapping.segment_name, null)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                title="Eliminar mapeo"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Existing Lists */}
                    <div>
                      <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                        Listas existentes en Brevo
                      </h3>
                      {lists.length === 0 ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          No hay listas creadas en Brevo
                        </p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {lists.map((list) => (
                            <div
                              key={list.id}
                              className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg"
                            >
                              <p className="font-medium text-slate-900 dark:text-white text-sm">
                                {list.name}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {list.total_subscribers} contactos
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
